/**
 * FieldTree Component
 *
 * Tree view container that renders grouped fields.
 * Handles filtering, grouping, and empty state.
 */

import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { FieldTreeGroup } from './FieldTreeGroup';
import { filterFields, groupFieldsByKey, sortGroups, buildFieldIndices } from './utils';
import type { FieldTreeProps } from './types';

export function FieldTree({
  fields,
  groups,
  selectedField,
  searchQuery,
  onSelect,
  focusedIndex,
}: FieldTreeProps) {
  const { t } = useTranslation('common');

  // Filter fields by search query
  const filteredFields = useMemo(
    () => filterFields(fields, searchQuery),
    [fields, searchQuery]
  );

  // Group filtered fields
  const groupedFields = useMemo(
    () => groupFieldsByKey(filteredFields, groups),
    [filteredFields, groups]
  );

  // Sort groups by order
  const sortedGroups = useMemo(() => sortGroups(groups), [groups]);

  // Build field indices for keyboard navigation
  const fieldIndices = useMemo(() => buildFieldIndices(filteredFields), [filteredFields]);

  // Filter out empty groups
  const nonEmptyGroups = sortedGroups.filter((group) => {
    const groupFields = groupedFields.get(group.key);
    return groupFields && groupFields.length > 0;
  });

  const isSearching = searchQuery.trim().length > 0;

  // Empty state
  if (nonEmptyGroups.length === 0) {
    return (
      <div className="px-4 py-8 text-center">
        <p className="text-sm text-fg-muted">
          {searchQuery ? (
            <>
              {t('fieldPicker.noFieldsMatching', 'No fields matching')} &quot;
              <span className="font-medium text-fg">{searchQuery}</span>
              &quot;
            </>
          ) : (
            t('fieldPicker.noFieldsAvailable', 'No fields available')
          )}
        </p>
      </div>
    );
  }

  return (
    <div
      style={{ maxHeight: '320px', overflowY: 'auto' }}
      onWheel={(e) => e.stopPropagation()}
    >
      <div
        className="px-1 py-1"
        role="tree"
        aria-label={t('fieldPicker.fieldSourcesAria')}
      >
        {nonEmptyGroups.map((group) => {
          const groupFields = groupedFields.get(group.key) || [];

          return (
            <FieldTreeGroup
              key={group.key}
              group={group}
              fields={groupFields}
              selectedField={selectedField}
              onSelect={onSelect}
              isSearching={isSearching}
              fieldIndices={fieldIndices}
              focusedIndex={focusedIndex}
            />
          );
        })}
      </div>
    </div>
  );
}

export default FieldTree;
